/**
 * Overlay Command Palette Registry
 *
 * Plugin-driven command registry for the global search overlay.
 * Commands can navigate, execute headless actions, or execute-then-navigate.
 * All commands are registered by plugins or core — none are hard-coded in the overlay.
 *
 * Uses Zustand for state management, consistent with other registries.
 */

import { create } from 'zustand';
import type { ComponentType } from 'react';

// ─── Types ──────────────────────────────────────────────────────

/**
 * Context passed to commands for availability checks and execution.
 * Extensible — plugins can read any field they need.
 */
export interface CommandContext {
  /** Current route path */
  currentRoute?: string;
  /**
   * Allow arbitrary extension data from plugins.
   * Plugins can attach domain-specific context (e.g., currentMeetingId)
   * without requiring core to know about the domain.
   */
  [key: string]: any;
}

/**
 * Result returned by a command's execute() function.
 * Determines what the overlay does after execution.
 */
export type CommandExecutionResult =
  | { kind: 'navigate'; to: string }
  | { kind: 'executed' }
  | { kind: 'executed-and-navigate'; to: string };

/**
 * Icon component type accepted by commands.
 * Matches the pattern used by lucide-react and the design system.
 */
export type CommandIconComponent = ComponentType<{ size?: number }>;

/**
 * Definition of a single command registered by a plugin or core.
 */
export interface CommandDefinition {
  /** Unique command ID (e.g., "create.meeting", "todos.create") */
  id: string;
  /** Human-readable title shown in the overlay (e.g., "Create note") */
  title: string;
  /** Optional description (e.g., "Open note editor") */
  description?: string;
  /** Optional icon component */
  icon?: CommandIconComponent;
  /** Keywords for fuzzy matching (in addition to title/description) */
  keywords?: string[];
  /** Group label for visual grouping (e.g., "Meetings", "Todos", "Notes") */
  group?: string;
  /**
   * Dynamic availability check.
   * If provided and returns false, the command is hidden from results.
   */
  isAvailable?: (ctx: CommandContext) => boolean;
  /**
   * Execute the command.
   * Returns a result indicating whether to navigate, stay, or both.
   */
  execute: (
    ctx: CommandContext,
    args?: Record<string, any>,
  ) => Promise<CommandExecutionResult>;
}

/**
 * A command that matched the current search query, with a relevance score.
 */
export interface CommandMatch {
  command: CommandDefinition;
  score: number;
}

// ─── Search / Filtering ─────────────────────────────────────────

/**
 * Score a command against a query string.
 * Returns 0 if no match, higher numbers = more relevant.
 */
function scoreCommand(command: CommandDefinition, query: string): number {
  const q = query.toLowerCase();
  const words = q.split(/\s+/).filter(Boolean);
  let score = 0;

  // Title match (highest weight)
  const titleLower = command.title.toLowerCase();
  if (titleLower === q) {
    score += 200;
  } else if (titleLower.startsWith(q)) {
    score += 150;
  } else if (titleLower.includes(q)) {
    score += 100;
  } else {
    for (const w of words) {
      if (titleLower.includes(w)) score += 50;
    }
  }

  // Description match
  if (command.description) {
    const descLower = command.description.toLowerCase();
    if (descLower.includes(q)) {
      score += 30;
    } else {
      for (const w of words) {
        if (descLower.includes(w)) score += 15;
      }
    }
  }

  // Keyword match
  if (command.keywords) {
    for (const kw of command.keywords) {
      const kwLower = kw.toLowerCase();
      if (kwLower.includes(q)) {
        score += 40;
      } else {
        for (const w of words) {
          if (kwLower.includes(w)) score += 20;
        }
      }
    }
  }

  // Group match (low weight — assists grouping relevance)
  if (command.group && command.group.toLowerCase().includes(q)) {
    score += 10;
  }

  // ID match (allows power users to type command IDs)
  if (command.id.toLowerCase().includes(q)) {
    score += 25;
  }

  return score;
}

/**
 * Filter and rank commands against a query, respecting availability.
 */
function filterCommands(
  commands: Map<string, CommandDefinition>,
  query: string,
  ctx: CommandContext,
): CommandMatch[] {
  const matches: CommandMatch[] = [];

  for (const [, command] of commands) {
    // Check availability
    if (command.isAvailable && !command.isAvailable(ctx)) continue;

    const score = query ? scoreCommand(command, query) : 1; // show all when no query
    if (score > 0) {
      matches.push({ command, score });
    }
  }

  matches.sort((a, b) => b.score - a.score);
  return matches;
}

// ─── Store ──────────────────────────────────────────────────────

interface OverlayCommandRegistryState {
  /** All registered commands */
  commands: Map<string, CommandDefinition>;

  // ── Actions ──
  /** Register a command definition */
  registerCommand: (def: CommandDefinition) => void;
  /** Unregister a command by ID */
  unregisterCommand: (id: string) => void;
  /** Get a command by ID */
  getCommand: (id: string) => CommandDefinition | undefined;
  /** Get all registered commands */
  getAllCommands: () => CommandDefinition[];
  /**
   * Search commands by query, filtered by context.
   * Returns scored matches sorted by relevance.
   */
  searchCommands: (query: string, ctx: CommandContext) => CommandMatch[];
}

export const useOverlayCommandRegistry = create<OverlayCommandRegistryState>(
  (set, get) => ({
    commands: new Map(),

    registerCommand: (def: CommandDefinition) => {
      set((state) => {
        const next = new Map(state.commands);
        next.set(def.id, def);
        return { commands: next };
      });
    },

    unregisterCommand: (id: string) => {
      set((state) => {
        const next = new Map(state.commands);
        next.delete(id);
        return { commands: next };
      });
    },

    getCommand: (id: string) => get().commands.get(id),

    getAllCommands: () => Array.from(get().commands.values()),

    searchCommands: (query: string, ctx: CommandContext) =>
      filterCommands(get().commands, query, ctx),
  }),
);

// ─── Convenience hooks ──────────────────────────────────────────

/**
 * Hook for components that need to register commands (typically in useEffect).
 */
export function useRegisterCommand() {
  const register = useOverlayCommandRegistry((s) => s.registerCommand);
  const unregister = useOverlayCommandRegistry((s) => s.unregisterCommand);
  return { registerCommand: register, unregisterCommand: unregister };
}

/**
 * Hook for the overlay to search commands.
 */
export function useCommandSearch() {
  const searchCommands = useOverlayCommandRegistry((s) => s.searchCommands);
  return { searchCommands };
}
